<template>
  <article class="news-card" :class="{'news-card--read': isInHistory}">
    <!-- Imagen principal (en lugar del header y avatar) -->
    <div class="news-card__image">
      <img :src="item.image" :alt="item.title" @error="onImageError" />
      <div class="news-card__image-overlay"></div>
    </div>

    <!-- Contenido del card -->
    <div class="news-card__content" :class="{
      'news-card__content--no-description': !item.description && item.sourceType === 'notice'
    }">
      <h3 class="news-card__title">{{ item.title }}</h3>
      <p class="news-card__category">{{ item.sourceTitle || item.sourceCategory || item.category }}</p>
      <p class="news-card__description" v-if="item.description">{{ item.description }}</p>
    </div>

    <!-- Metadata: fecha, favorito y leído -->
    <div class="news-card__metadata">
      <span class="metadata-date">
        {{ timeAgo(item.pubDate) }}
        <span v-if="item.sourceCountry" class="metadata-country"> • {{ item.sourceCountry }}</span>
      </span>
      <div class="metadata-actions">
        <!-- Botón compartir -->
        <div class="share-btn-wrapper" v-if="!isModal">
          <button
            @click.stop="shareCard"
            class="metadata-btn"
            :class="{'metadata-btn--copied': showCopied}"
            :title="showCopied ? '¡Copiado!' : 'Compartir'"
          >
            <svg v-if="!showCopied" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="18" cy="5" r="3"/>
              <circle cx="6" cy="12" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </button>

          <!-- Tooltip "Enlace copiado" -->
          <transition name="tooltip-fade">
            <div v-if="showCopied" class="share-tooltip">
              Enlace copiado
            </div>
          </transition>
        </div>

        <!-- Botón favorito -->
        <div class="favorite-btn-wrapper">
          <button
            @click.stop="toggleFavorite"
            class="metadata-btn"
            :class="{'metadata-btn--active': isFavorite}"
            :title="isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </button>

          <!-- Tooltip de favorito -->
          <transition name="tooltip-fade">
            <div v-if="showFavoriteTooltip" class="favorite-tooltip">
              {{ favoriteTooltipText }}
            </div>
          </transition>
        </div>

        <!-- Indicador de leído -->
        <button
          v-if="isInHistory"
          class="metadata-btn metadata-btn--active"
          title="Leído"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Botón leer más -->
    <div class="news-card__footer">
      <button class="news-card__button" @click.stop="openLink">
        Leer más
      </button>
    </div>
  </article>
</template>

<script>
import { timeAgo, hostname } from '../../../utils/time.js';
import bookmarksService from '../../../services/bookmarks.js';
import { copyShareUrl } from '../../../utils/shareUtils.js';

export default {
  name: 'NewsWithImageCard',
  props: {
    item: {
      type: Object,
      required: true
    },
    isModal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      updateTrigger: 0,
      // Estado del botón compartir
      showCopied: false,
      // Estado del tooltip de favorito
      showFavoriteTooltip: false,
      favoriteTooltipText: ''
    };
  },
  computed: {
    isFavorite() {
      this.updateTrigger;
      return bookmarksService.isFavorite(this.item.id || this.item.link);
    },
    isInHistory() {
      this.updateTrigger;
      return bookmarksService.isInHistory(this.item.id || this.item.link);
    }
  },
  methods: {
    timeAgo,
    hostname,
    openLink() {
      window.open(this.item.link, '_blank', 'noopener,noreferrer');
      bookmarksService.addToHistory(this.item);
    },
    toggleFavorite() {
      if (this.isFavorite) {
        bookmarksService.removeFromFavorites(this.item.id || this.item.link);
        this.favoriteTooltipText = 'Quitado de favoritos';
      } else {
        bookmarksService.addToFavorites(this.item);
        this.favoriteTooltipText = 'Agregado a favoritos';
      }

      // Mostrar tooltip
      this.showFavoriteTooltip = true;
      setTimeout(() => {
        this.showFavoriteTooltip = false;
      }, 2000);
    },
    async shareCard() {
      const success = await copyShareUrl(this.item);

      if (success) {
        // Mostrar feedback visual
        this.showCopied = true;
        setTimeout(() => {
          this.showCopied = false;
        }, 2000);
      } else {
        console.error('Failed to copy share URL');
      }
    },
    onImageError(event) {
      // Si la imagen falla al cargar, mostrar un placeholder gris
      event.target.style.display = 'none';
      event.target.parentElement.style.background = '#262c34';
    },
    handleBookmarkUpdate() {
      this.updateTrigger++;
    }
  },
  mounted() {
    window.addEventListener('bookmarks-updated', this.handleBookmarkUpdate);
  },
  beforeUnmount() {
    window.removeEventListener('bookmarks-updated', this.handleBookmarkUpdate);
  }
};
</script>

<style scoped>
/* Diseño tipo card con imagen destacada */
.news-card {
  background: #1c2127;
  border: 1px solid #2d333b;
  border-radius: 14px;
  padding: 0;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.news-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
}

/* Imagen principal con overlay sutil */
.news-card__image {
  width: 100%;
  height: 150px;
  position: relative;
  overflow: hidden;
  background: #262c34;
}

.news-card__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.news-card:hover .news-card__image img {
  transform: scale(1.05);
}

.news-card__image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to top, rgba(28, 33, 39, 0.8), transparent);
  pointer-events: none;
}

/* Contenido del card */
.news-card__content {
  padding: 14px 16px 12px;
  text-align: center;
  flex: 1;
}

.news-card__title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
  color: #e1e7ef;
  margin: 0 0 6px 0;
  overflow-wrap: anywhere;
}

.news-card__category {
  font-size: 13px;
  font-weight: 500;
  color: #4a9eff;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.news-card__description {
  font-size: 13px;
  line-height: 1.5;
  color: #9ca3af;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Metadata: fecha y acciones */
.news-card__metadata {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-top: 1px solid #2d333b;
  margin: 0 16px;
}

.metadata-date {
  font-size: 13px;
  color: #9ca3af;
  font-weight: 500;
}

.metadata-country {
  color: #7d8694;
  font-size: 12px;
  font-weight: 400;
}

.metadata-actions {
  display: flex;
  gap: 8px;
}

.metadata-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1.5px solid #2d333b;
  background: #1c2127;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.metadata-btn:hover {
  border-color: #4a5568;
  background: #262c34;
}

.metadata-btn--active {
  background: white;
  border-color: white;
  color: #1c2127;
}

.metadata-btn--active:hover {
  background: #f3f4f6;
  border-color: #f3f4f6;
}

.metadata-btn--copied {
  background: #34d399;
  border-color: #34d399;
  color: white;
}

.metadata-btn--copied:hover {
  background: #10b981;
  border-color: #10b981;
}

/* Wrapper del botón compartir con tooltip */
.share-btn-wrapper {
  position: relative;
  display: inline-block;
}

/* Tooltip "Enlace copiado" */
.share-tooltip {
  /* Anclado a la derecha, igual que el de favorito */
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  background: #1c2127;
  color: #34d399;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border: 1px solid #34d399;
  z-index: 100;
  pointer-events: none;
}

/* Flecha del tooltip */
.share-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  right: 9px;
  border: 6px solid transparent;
  border-top-color: #34d399;
}

/* Animación del tooltip */
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: all 0.2s ease;
}

.tooltip-fade-enter,
.tooltip-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-4px);
}

/* Wrapper del botón favorito con tooltip */
.favorite-btn-wrapper {
  position: relative;
  display: inline-block;
}

/* Tooltip de favorito */
.favorite-tooltip {
  /* Anclado a la derecha: centrado se sale de la pantalla en móvil */
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  background: #1c2127;
  color: #fbbf24;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border: 1px solid #fbbf24;
  z-index: 100;
  pointer-events: none;
}

/* Flecha del tooltip favorito */
.favorite-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  right: 9px;
  border: 6px solid transparent;
  border-top-color: #fbbf24;
}

.share-tooltip.tooltip-fade-enter,
.share-tooltip.tooltip-fade-leave-to,
.favorite-tooltip.tooltip-fade-enter,
.favorite-tooltip.tooltip-fade-leave-to {
  transform: translateY(-4px);
}

/* Footer con botón */
.news-card__footer {
  display: flex;
  justify-content: center;
  padding: 0 16px 14px;
}

.news-card__button {
  padding: 8px 24px;
  background: white;
  border: none;
  border-radius: 50px;
  color: #1c2127;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.1);
  width: 100%;
  max-width: 180px;
}

.news-card__button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 255, 255, 0.2);
  background: #f3f4f6;
}

/* Card leído - estilo más suave */
.news-card--read {
  opacity: 0.85;
}

.news-card--read .news-card__title {
  color: #a0aec0;
}

.news-card--read:hover {
  opacity: 1;
}

.news-card--read:hover .news-card__title {
  color: #e1e7ef;
}

/* Responsive design */
@media (max-width: 768px) {
  .news-card__image {
    height: 140px;
  }

  .news-card__title {
    font-size: 15px;
  }

  .news-card__category {
    font-size: 12px;
  }

  .metadata-date {
    font-size: 12px;
  }
}
</style>
